import { Avatar, Tag, Tooltip, theme } from "antd";
import { Flex } from "src/components/Flex";
import { IntegrationIcon } from "src/components/IntegrationIcon";
import { useOrg } from "src/hooks/useOrg";
import { formatTime, isStringDate } from "src/utils/date";
import { CollapsibleRow } from "./CollapsibleRow";

type TimeMachineItem = {
  uuid: string;
  delta: Record<string, { before: any; after: any }>;
  occurredAt: string;
  event: Record<string, any>;
  actor: Record<string, any>;
};

export const TimeMachineCard = (props: { event: TimeMachineItem }) => {
  const { token } = theme.useToken();
  const orgId = useOrg().id;
  const record = props.event;
  const changedFields = Object.keys(record.delta);
  const changeset = changedFields.map((f) => ({
    field: f,
    before: record.delta[f].before,
    after: record.delta[f].after,
  }));
  return (
    <CollapsibleRow
      expand={true}
      preview={
        <div
          className="clickable-row"
          style={{
            padding: "8px",
            borderBottom: "1px solid var(--color-border)",
          }}
          onClick={() =>
            window.open(`/${orgId}/entity/${record.uuid}`, "_blank")
          }
        >
          <Flex spaceBetween>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Flex start>
                <Avatar
                  shape="square"
                  size="small"
                  src={record.actor?.avatarUrl || undefined}
                  style={{
                    width: 28,
                    height: 28,
                  }}
                >
                  {record.actor?.name
                    .split(" ")
                    .map((name: string) => (name[0] || "").toLocaleUpperCase())
                    .join("")}
                </Avatar>
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    paddingLeft: 4,
                    paddingRight: 4,
                  }}
                >
                  <div
                    style={{
                      lineHeight: "1em",
                      overflow: "visible",
                      fontSize: 11,
                      color: token.colorTextTertiary,
                      fontWeight: 500,
                    }}
                    className="open-entity"
                  >
                    <span style={{ opacity: 0.7 }}> on </span>
                    {formatTime(record.occurredAt, "MMMM D, YYYY")}
                    <span style={{ opacity: 0.7 }}> at </span>
                    {formatTime(record.occurredAt, "h:mm a")}
                  </div>
                  <Tooltip
                    mouseEnterDelay={1}
                    mouseLeaveDelay={0}
                    title={record.event.description}
                    placement="topLeft"
                  >
                    <div
                      className="open-entity trunc"
                      style={{ paddingTop: 2, lineHeight: 1.1, fontSize: 14 }}
                    >
                      {record.event.description}
                    </div>
                  </Tooltip>
                </div>
              </Flex>
            </div>

            <div
              className="open-entity"
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 5,
                minWidth: 0,
              }}
            >
              {record.event?.Action && (
                <Tag className="tag">{record.event?.Action}</Tag>
              )}
              {record.event?.EntityType && (
                <Tag className="tag">{record.event?.EntityType}</Tag>
              )}
              <Tag className="tag">
                <IntegrationIcon
                  size={"xsmall"}
                  source={`${record.event?.integration}`}
                  style={{ marginRight: 10 }}
                />
                {record.event?.integration}
              </Tag>
            </div>
          </Flex>
        </div>
      }
      content={
        <ul
          style={{
            paddingTop: 8,
            paddingLeft: 60,
            lineHeight: 1.3,
            fontSize: 13,
          }}
        >
          {changeset.map(({ field, before, after }) => (
            <li key={field}>
              <span>{field}</span>&nbsp;
              <Tag>{formatValue(before, orgId)}</Tag>
              →&nbsp;
              <Tag>{formatValue(after, orgId)}</Tag>
            </li>
          ))}
        </ul>
      }
    />
  );
};

const formatValue = (v: any, orgId: string | undefined) => {
  if (v === undefined) return "none";
  if (Array.isArray(v)) {
    return v.join(", ");
  }
  if (typeof v === "object") {
    return (
      <a onClick={() => window.open(`/${orgId}/entity/${v.uuid}`, "_blank")}>
        {v.entityType}:{v.value}
      </a>
    );
  }
  if (isStringDate(v)) return formatTime(v);
  return `${v}`;
};
